import { hasUIDField, UIDContext } from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import { slugify } from "@prismicio/editor-support/String"
import {
  collectWidgets,
  Document as DocumentT,
  type FieldContent,
  type RichTextContent
} from "@prismicio/types-internal/lib/content"
import type { CustomType } from "@prismicio/types-internal/lib/customtypes"
import { useCallback, useContext, useMemo, useState } from "react"
import { v4 as createUUID } from "uuid"

import { getReleaseId } from "../app/services/release"
import { type DocumentData, type NewDocumentData } from "./documentData"

export function useDraft(data: DocumentData): DocumentDraft {
  const { type, customType } = data

  const { meta, version } =
    data.type === "existing" ? data : { meta: undefined, version: undefined }

  const document = getDocument()

  const metaId = meta?.id

  const [previousMetaId, setPreviousMetaId] = useState(metaId)
  const [nextAnimation, setNextAnimation] = useState<NextAnimation>()
  const [cancelSignal, setCancelSignal] = useState(0)

  function getDocument() {
    if (data.type === "existing") return data.document
    if (isNewDocFromCopy(data)) return data.asCopy.document
    return {}
  }

  function getDocData() {
    if (isNewDocFromCopy(data)) return data.asCopy.document
    if (data.type === "existing" && data.initialDraft !== undefined)
      return data.initialDraft

    return document
  }

  function getEditedAt() {
    if (isNewDocFromCopy(data)) return new Date()

    if (data.type === "existing" && data.initialDraft !== undefined)
      return new Date()

    return undefined
  }

  function getDocumentTags() {
    if (isNewDocFromCopy(data)) return new Set(data.asCopy.tags)

    return new Set(version?.tags ?? [])
  }

  const [draft, setDraft] = useState<Draft>({
    id: createUUID(),
    document: getDocData(),
    documentTags: getDocumentTags(),
    editedAt: getEditedAt()
  })

  const { editedUID } = useContext(UIDContext)

  const edit = useCallback(
    (document: DocumentT) => {
      return setDraft((prevDraft) => ({
        ...prevDraft,
        editedAt: new Date(),
        document: transformDocumentAfterEdit({
          document,
          customType,
          type,
          editedUID: editedUID.current
        })
      }))
    },
    [type, customType, editedUID]
  )

  const resetDraft = useCallback(
    (animate = false) =>
      setDraft((draft) => ({
        id: animate ? createUUID() : draft.id,
        document,
        documentTags: new Set(version?.tags ?? [])
      })),
    [document, version]
  )

  const animateNextChange = useCallback((toVersionId?: string) => {
    setNextAnimation({ toVersionId })
  }, [])

  const cancel = useCallback(() => {
    setCancelSignal((signal) => signal + 1)
  }, [])

  const metaIdChanged = metaId !== previousMetaId
  if (metaIdChanged) setPreviousMetaId(metaId)

  const releaseId = version ? getReleaseId(version) : undefined

  // Batch and only reset a single time.
  const resetDraftSignal = useMemo(
    () => [cancelSignal, version?.status, version?.version_id, releaseId],
    [cancelSignal, releaseId, version?.status, version?.version_id]
  )

  useOnChange(resetDraftSignal, () => {
    // When the meta.id changes, the version_id always changes as well and
    // it's a big enough change that we want to force the animation.
    // Typically this happen when navigating with the previous button, etc.
    resetDraft(Boolean(nextAnimation) || metaIdChanged)
    setNextAnimation(undefined)
  })

  const updateTags = useCallback(
    (newTags: Set<string>) =>
      setDraft((prevDraft) => ({
        ...prevDraft,
        editedAt: new Date(),
        documentTags: newTags
      })),
    []
  )

  return useMemo(
    () => ({
      ...draft,
      edit,
      cancel,
      nextAnimation,
      animateNextChange,
      updateTags
    }),
    [draft, edit, cancel, nextAnimation, updateTags, animateNextChange]
  )
}

export interface DocumentDraft {
  // The draft id is used to keep a stable reference after each draft reset.
  // This helps run animations when a significant change occured.
  id: string
  document: DocumentT
  editedAt?: Date
  edit: (newDocument: DocumentT) => void
  documentTags: Set<string>
  nextAnimation: NextAnimation
  updateTags: (newTags: Set<string>) => void
  cancel: () => void
  animateNextChange: (toVersionId?: string) => void
}

type NextAnimation = { toVersionId?: string } | undefined

export interface Draft {
  id: string
  editedAt?: Date
  document: DocumentT
  documentTags: Set<string>
}

type NewDocFromCopy = NewDocumentData & {
  asCopy: NonNullable<NewDocumentData["asCopy"]>
}

function isNewDocFromCopy(doc: DocumentData): doc is NewDocFromCopy {
  return doc.type === "new" && Boolean(doc.asCopy)
}

interface TransformDocumentAfterEditParams {
  type: "new" | "existing"
  document: DocumentT
  customType: CustomType
  editedUID: boolean
}

function transformDocumentAfterEdit(params: TransformDocumentAfterEditParams) {
  const { type, document, customType, editedUID } = params

  const shouldGenerateUID =
    type === "new" && hasUIDField(customType) && !editedUID

  if (!shouldGenerateUID) return document

  return setGeneratedUID(document)
}

export function setGeneratedUID(content: DocumentT): DocumentT {
  const generatedUID = extractRelevantTextFromContent(content)
  if (!generatedUID) return content

  return {
    ...content,
    uid: {
      __TYPE__: "UIDContent",
      value: slugify(generatedUID).substring(0, 50)
    }
  }
}

const headingTypePriority = [
  "heading1",
  "heading2",
  "heading3",
  "heading4",
  "heading5",
  "heading6",
  "paragraph"
] as const

export function extractRelevantTextFromContent(
  content: DocumentT
): string | undefined {
  // Try to find a heading with content, by order of importance
  for (const headingType of headingTypePriority) {
    const headingValue = getRichTextContentsByType(content, headingType)

    if (headingValue.length > 0) return headingValue[0]
  }

  // If no headings were found, find keytext fields that can do this same
  const keyTexts = collectWidgets(
    content,
    (widget): widget is FieldContent =>
      widget.__TYPE__ === "FieldContent" && widget.type === "Text"
  )

  const keyTextValues = Object.values(keyTexts).map((content) => content.value)

  // If no keytexts have content, then leave the UID undefined
  if (keyTextValues.length === 0) return

  return keyTextValues[0]
}

type HeadingType = typeof headingTypePriority[number]

export function getRichTextContentsByType(
  content: DocumentT,
  type: HeadingType
) {
  const richTexts = collectWidgets(
    content,
    (widget): widget is RichTextContent =>
      widget.__TYPE__ === "StructuredTextContent"
  )

  const richTextsValues = Object.values(richTexts).reduce<string[]>(
    (acc, content) => {
      const contentValues = getContentTextValues(content.value, type)

      return [...acc, ...contentValues]
    },
    []
  )

  return richTextsValues
}

function getContentTextValues(
  richTextValue: RichTextContent["value"],
  textType: HeadingType
) {
  const contentValues = richTextValue.reduce<string[]>(
    (contentValues, contentValue) => {
      if (contentValue.type !== textType) return contentValues

      return [...contentValues, contentValue.content.text]
    },
    []
  )

  return contentValues
}
